import { useCallback } from 'react';
import { trackError } from '../../utils/error-tracking';
import type { GuideArticle } from '../../data/guide-articles';
import type { GuideIndexEntry } from './GuideSelectionPopup';

type GuideCategory = 'seller' | 'buyer';

interface FetchGuideResult {
  type: 'article';
  guide: GuideArticle;
}

interface FetchGuideIndexResult {
  type: 'index';
  guides: GuideIndexEntry[];
}

type FetchResult = FetchGuideResult | FetchGuideIndexResult | null;

export function useGuideSlideshow(
  guideCategory: GuideCategory,
  guideSlug?: string,
) {
  const fetchGuideData = useCallback(async (): Promise<FetchResult> => {
    try {
      if (guideSlug) {
        const res = await fetch(`/data/guides/${guideCategory}--${guideSlug}.json`);
        if (!res.ok) return null;
        const guide: GuideArticle = await res.json();
        return { type: 'article', guide };
      }
      const res = await fetch(`/data/guides/${guideCategory}--index.json`);
      if (!res.ok) return null;
      const guides: GuideIndexEntry[] = await res.json();
      return { type: 'index', guides };
    } catch (error) {
      trackError(
        error instanceof Error ? error : new Error('Failed to fetch guide data'),
        'warning',
        { component: 'useGuideSlideshow', extra: { guideCategory, guideSlug } },
      );
      return null;
    }
  }, [guideCategory, guideSlug]);

  return { fetchGuideData };
}
